import { useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { createPortal } from 'react-dom';
import './Modal.css';

const Modal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  children,
  size = 'medium',
  confirmText = __('Save', 'havenlytics'),
  cancelText = __('Cancel', 'havenlytics'),
  showConfirm = true,
  showCancel = true,
  isLoading = false,
  closeOnOverlayClick = true,
}) => {
  const modalRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
      // Add class to body for additional styling if needed
      document.body.classList.add('hvnly-modal-open');
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
      document.body.classList.remove('hvnly-modal-open');
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm && !isLoading) {
      onConfirm();
    }
  };

  if (!isOpen || !mounted) return null;

  const sizeClasses = {
    small: 'hvnly-modal--small',
    medium: 'hvnly-modal--medium',
    large: 'hvnly-modal--large',
    xlarge: 'hvnly-modal--xlarge',
  };

  // Render modal directly to document.body using Portal
  return createPortal(
    <div className="hvnly-modal-overlay" onClick={handleOverlayClick}>
      <div ref={modalRef} className={`hvnly-modal ${sizeClasses[size] || sizeClasses.medium}`}>
        <div className="hvnly-modal__header">
          <h2 className="hvnly-modal__title">{title}</h2>
          <button className="hvnly-modal__close-btn" onClick={onClose} aria-label={__('Close', 'havenlytics')}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="hvnly-modal__body">{children}</div>

        {(showConfirm || showCancel) && (
          <div className="hvnly-modal__footer">
            {showCancel && (
              <button type="button" className="hvnly-modal__btn hvnly-modal__btn--secondary" onClick={onClose} disabled={isLoading}>
                {cancelText}
              </button>
            )}
            {showConfirm && (
              <button type="button" className="hvnly-modal__btn hvnly-modal__btn--primary" onClick={handleConfirm} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>{__('Saving...', 'havenlytics')}</span>
                  </>
                ) : (
                  confirmText
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;